import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import * as moment from 'moment';

import { Constants } from 'src/shared/utilities/constants.util';
import { EntityAdminConfig } from 'src/shared/Baseclasses/entity-admin.component';
import { Facility } from 'src/shared/models/facility.model';
import { FacilityService } from 'src/shared/services/facility.service';
import { PageLayoutService } from 'src/shared/services/page-layout.service';
import { TimeZone } from 'src/shared/models/time-zone.model';
import { TimeZoneService } from 'src/shared/services/time-zone.service';
import { InpatientShiftRecordService } from 'src/shared/services/shift-record-inpatient.service';

@Component({
    selector: 'smt-facility',
    templateUrl: './facility.component.html',
    styleUrls: ['./facility.component.scss']
})
export class FacilityComponent extends EntityAdminConfig implements OnDestroy, OnInit {
    @ViewChild('facilityEditForm') public facilityEditForm: NgForm;

    facilities: Facility[];
    facility: Facility;
    selectedName: string;
    selectedAcronym: string;
    selectedPayPeriodStartDate: string;
    selectedTimeZoneId: string;
    timeZones: TimeZone[] = [];
    hasWorkflowData: boolean; 

    constructor(
        private _facilityService: FacilityService, 
        private _pageLayout: PageLayoutService, 
        private _timeZoneService: TimeZoneService, 
        private _shiftRecordService: InpatientShiftRecordService) { 
        super(_pageLayout); 
    }

    ngOnDestroy() {
		super.cleanUp();
    }
  
    async ngOnInit() {
        super.initialize(
            Constants.Facilities,
            [ "Name", "Acronym", "Last Modified" ], 
            async() => this.setSelectedFacilityValues(), 
            async(mostRecent: boolean) => this.refreshFacilityList(mostRecent), 
            async() => this.appendFacilityList(),
            async() => this.saveFacility(),
            () => this.setCurrentSelectionLabel(),
            () => { return "Remove"; });

        this.timeZones = await this._timeZoneService.getUnitedStatesTimeZones();
    }

    async appendFacilityList(): Promise<void> {
        let result = await this._facilityService.getFacilitiesInfiniteScroll(
                                                false,
                                                this.offset, 
                                                this.itemsPerPage, 
                                                this.searchText, 
                                                this.selectedFilter, 
                                                this.getSortOrderString());

        this.facilities = this.facilities.concat(result);		
    } 

    onAddFacilityClick() { 
        this.selection = new Facility();        
        
        this.selection.acronym = "";
        this.selection.id = 0;
        this.selection.name = "";
        this.selection.timeZoneId = "";
        this.selection.payPeriodStartDate = null;
    
        this.showAdminActionWindow = true;
        super.limitAdminActionLabel();
		this.setSelectedFacilityValues();
    }

    onCancel() {
		super.onCancel();
		this.setSelectedFacilityValues();
		this.resetForm();
    }

     onSelection(facility: Facility) {
        this.resetForm();
        super.onSelection(facility);
    }

    async refreshFacilityList(mostRecent: boolean): Promise<Facility[]> {
        let result = await this._facilityService.getFacilitiesInfiniteScroll(
                                                    mostRecent,
                                                    this.offset, 
                                                    this.itemsPerPage, 
                                                    this.searchText, 
                                                    this.selectedFilter, 
                                                    this.getSortOrderString());    
        this.facilities = result;

        this.setSelectedFacilityValues();
        this.resetForm();
        return result;
    }

    async removeFacility()
	{		
		if(!this.selection) {
			return;
        }
    
        let result = await this._facilityService.removeFacility(this.selection.id);
        this.confirmModalActive = false;
        if (result) {
            super.refreshAdminList(false, false);
        }
        else {
            super.setErrorMessage("Facility delete failed.");
        }	
    } 

    resetForm() {
		if (!this.facilityEditForm) {
			return;
		}

		Object.keys(this.facilityEditForm.controls).forEach(control => {
			this.facilityEditForm.controls[control].markAsPristine();
		});
    }

    async saveFacility() {
        let mostRecent = this.selection.id == 0;

        this.facility = new Facility();
        this.facility.id = this.selection.id;
        this.facility.name = this.selectedName;
        this.facility.acronym = this.selectedAcronym;
        this.facility.payPeriodStartDate = moment(this.selectedPayPeriodStartDate).toDate();
        this.facility.timeZoneId = this.selectedTimeZoneId;
    
        let existingFacility = await this._facilityService.getFacilityByName(this.facility.name);

        if (existingFacility && existingFacility.id != this.facility.id) {
            super.setErrorMessage("Save failed.  The entered facility name is a duplicate.");
            return;
        }

        existingFacility = await this._facilityService.getFacilityByAcronym(this.facility.acronym);
        if (existingFacility && existingFacility.id != this.facility.id) {
            super.setErrorMessage("Save failed.  The entered facility acronym is a duplicate.");
            return;
        }
    
        let result = await this._facilityService.setFacility(this.facility);
        this.selection.id = result;
        this.selection.name = this.facility.name;
        this.selection.acronym = this.facility.acronym;
        this.selection.payPeriodStartDate = this.facility.payPeriodStartDate;
        this.selection.timeZoneId = this.facility.timeZoneId;                
    
        await super.refreshAdminList(mostRecent, true);

        if (result > 0) {      
            this.resetForm();
        }
        else {
            super.setErrorMessage("Facility failed to save.");
        }   
    }

    setCurrentSelectionLabel(): string {
        if (!this.selection) {
            return "";
        }

        if (this.selection.id == 0) {
            return "New Facility";
        }

        return this.selection.name;
    }  

    setSelectedFacilityValues() {
        super.setErrorMessage("");
        
        if (this.selection) {
            // After each selection we assume it has workflow data so that it disables the proper controls.
            // This assumption only needs to be made if the selection has a legitimate id, which indicates
            // that it is a facility pulled from the database.
            // The controls will only enable once it has been proven to not have workflow data.
            this.hasWorkflowData = this.selection.id > 0;
            
            this.selectedName = this.selection.name;
            this.selectedAcronym = this.selection.acronym;
            this.selectedTimeZoneId = this.selection.timeZoneId;

            if (this.selection.payPeriodStartDate) {
                this.selectedPayPeriodStartDate = moment(this.selection.payPeriodStartDate).format('YYYY-MM-DD');
            }
            else {
                this.selectedPayPeriodStartDate = "";
            }                        

            // Only check the database to determine if this facility has workflow data if the id indicates
            // that this facility exists in the DB.  There is no need to check this if the facility is a
            // new facility that hasn't been saved to the database yet.
            if (this.selection.id > 0) {
                this._shiftRecordService.hasFacilityWorkflowData(this.selection.id).then(hasData => {
                    this.hasWorkflowData = hasData;
                });
            }    
        }
        else {
            this.selectedName = "";
            this.selectedAcronym = "";
            this.selectedPayPeriodStartDate = "";
            this.selectedTimeZoneId = "";
        }
    }
}
